/**
 * Stale / invalidated SA handoff detection (plan §7.4 / G17 / AC-05–06).
 * Block pay when session abandoned/revoked or basket version mismatch.
 */
import { ApiError } from '../../shared/api/ApiClient.js';
import {
  isSellerAssistedCheckoutOrigin,
  type SellerAssistedOriginSessionFields,
} from './isSellerAssistedCheckoutOrigin.js';

/** Stable API codes that mean staff invalidated the payable context. */
export const SELLER_ASSISTED_STALE_HANDOFF_ERROR_CODES = [
  'SELLER_ASSISTED_CUSTOMER_MUTATION_FORBIDDEN',
  'SELLER_ASSISTED_BASKET_NOT_EDITABLE',
  'INVALID_SELLER_ASSISTED_TRANSITION',
  'CHECKOUT_SESSION_INVALID_STATE',
  'HANDOFF_REVOKED',
  'HANDOFF_EXPIRED',
  'PAYMENT_HANDOFF_REVOKED',
  'BASKET_VERSION_MISMATCH',
  'PRICE_CHANGED',
] as const;

export type SellerAssistedStaleHandoffErrorCode =
  (typeof SELLER_ASSISTED_STALE_HANDOFF_ERROR_CODES)[number];

const STALE_CODE_SET = new Set<string>(SELLER_ASSISTED_STALE_HANDOFF_ERROR_CODES);

export function isSellerAssistedStaleHandoffErrorCode(code: string | null | undefined): boolean {
  if (code == null || code.length === 0) {
    return false;
  }
  return STALE_CODE_SET.has(code);
}

export function isSellerAssistedStaleSessionStatus(
  status: string | null | undefined,
): boolean {
  return status === 'CANCELLED' || status === 'EXPIRED';
}

/**
 * Client expected basketVersion vs server — stale when client is behind.
 * When either side is missing, do not block (unknown ≠ stale).
 */
export function isSellerAssistedBasketVersionStale(input: {
  readonly clientBasketVersion?: number | null;
  readonly serverBasketVersion?: number | null;
}): boolean {
  const client = input.clientBasketVersion;
  const server = input.serverBasketVersion;
  if (
    client == null ||
    server == null ||
    !Number.isFinite(client) ||
    !Number.isFinite(server)
  ) {
    return false;
  }
  return client < server;
}

export function resolveSellerAssistedStaleHandoff(input: {
  readonly session: SellerAssistedOriginSessionFields | null | undefined;
  readonly clientBasketVersion?: number | null;
  readonly sessionError?: unknown;
}): {
  readonly stale: boolean;
  readonly reason: 'status' | 'version' | 'error' | null;
} {
  if (!isSellerAssistedCheckoutOrigin(input.session)) {
    return { stale: false, reason: null };
  }
  if (isSellerAssistedStaleSessionStatus(input.session?.status)) {
    return { stale: true, reason: 'status' };
  }
  if (
    isSellerAssistedBasketVersionStale({
      clientBasketVersion: input.clientBasketVersion,
      serverBasketVersion: input.session?.basketVersion,
    })
  ) {
    return { stale: true, reason: 'version' };
  }
  if (input.sessionError instanceof ApiError) {
    if (isSellerAssistedStaleHandoffErrorCode(input.sessionError.code)) {
      return { stale: true, reason: 'error' };
    }
  }
  return { stale: false, reason: null };
}

export function isSellerAssistedPaymentBlockedByStaleHandoff(input: {
  readonly session: SellerAssistedOriginSessionFields | null | undefined;
  readonly clientBasketVersion?: number | null;
  readonly sessionError?: unknown;
}): boolean {
  return resolveSellerAssistedStaleHandoff(input).stale;
}
